import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ProjectDataService } from '@project/service/project-data.service';
import { ProjectOrderDTO } from '@article/model/vo/project-order.dto';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectSaveInfoVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { lastValueFrom, Observable, of } from 'rxjs';
import { Log } from '@shared/debug/log';
import { catchError, map } from 'rxjs/operators';
import { EShopAddedToWatchlistResponse, EShopDataService } from '@project/service/eshop-data.service';
import { ToastrService } from 'ngx-toastr';
import { ComponentHotkeyService } from '@shared/component-hotkey.service';

@Component({
  standalone: false,
  selector: 'app-export-eshop-watchlist',
  templateUrl: './export-eshop-watchlist.component.html',
  styleUrls: ['./export-eshop-watchlist.component.scss'],
  providers: [ComponentHotkeyService],
})
export class ExportEshopWatchlistComponent {
  @Output() public finish = new EventEmitter<void>();

  public watchlistDataForm: FormGroup;
  public watchlistNameFormControl: FormControl;

  public existingWatchLists$: Observable<string[]>;

  constructor(
    private formBuilder: FormBuilder,
    private eShopDataService: EShopDataService,
    private projectDataService: ProjectDataService,
    private localeResourceManager: LocaleResourceManager,
    private projectModel: ProjectModel,
    private toastrService: ToastrService,
    private componentHotkeyService: ComponentHotkeyService,
  ) {
    this.existingWatchLists$ = this.eShopDataService.getWatchLists().pipe(
      map(
        (watchListResponse => watchListResponse.watchlists.map(watchList => watchList.name))
      ),
      catchError(err => {
        this.toastrService.error("Failed to load existing watchlists from WAGO eShop");
        return of([]);
      }),
    );
    this.watchlistNameFormControl = new FormControl("", Validators.required);
    this.watchlistDataForm = this.formBuilder.group({
      watchlistNameFormControl: this.watchlistNameFormControl,
    });

    this.componentHotkeyService.add("enter", () => {
      if (this.watchlistNameFormControl.valid) {
        this.export();
      }
    });
  }

  public async export(): Promise<void> {
    let projectSaveInfo: ProjectSaveInfoVO;
    try {
      projectSaveInfo = await lastValueFrom(this.projectDataService.loadProjectSaveInfo(this.projectModel.currentProject.id));
    } catch (error) {
      Log.error('Could not read project save info', error);
      throw error;
    }

    const projectOrder: ProjectOrderDTO = new ProjectOrderDTO();
    projectOrder.language = this.localeResourceManager.currentLanguage.langCode;
    projectOrder.orderType = "ESHOP_ADD_TO_WATCHLIST";
    projectOrder.projectId = projectSaveInfo.projectId;
    projectOrder.projectVersion = projectSaveInfo.highestFinalizedProjectVersion;
    projectOrder.watchList = this.watchlistNameFormControl.value;

    try {
      const result: EShopAddedToWatchlistResponse = await lastValueFrom(this.eShopDataService.addProjectToWatchlist(projectOrder));
      this.openWatchlistUrl(result.storeFrontUrl, result.watchListGuid);
      this.close();
    } catch (error) {
      this.toastrService.error("Failed to add articles to watchlist");
      Log.error('Error adding project to watchlist', error);
    }
  }

  public openWatchlistUrl(storeFrontUrl: string, watchlistGuid: string) {
    console.log(storeFrontUrl + "/watchlist/" + watchlistGuid)
    window.open(storeFrontUrl + "/watchlist/" + watchlistGuid, `_blank`);
  }

  public selectExisting(name: string) {
    this.watchlistNameFormControl.setValue(name);
  }

  public get projectName(): string {
    return this.projectModel.currentProject?.name ?? "";
  }

  public close(): void {
    this.finish.emit();
  }
}
